import { computed, ComputedRef, Ref, ref, watch } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { PayJoyCardStatus } from "@/apps/credit-line/models/PayJoyCardStatus";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { ICardInfo } from "@/apps/credit-line/models/CreditLineDetails";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import { useStore } from "vuex";

export function useCardStatus(): {
  payjoyCardStatus: Ref<PayJoyCardStatus | undefined>;
  disableCard: Ref<boolean | false>;
  selectedCard: ComputedRef<ICardInfo | undefined>;
  creditLineId: Ref<number | undefined>;
  alertMessage: Ref<string | undefined>;
  showAlert: Ref<boolean>;
  ccDeliveryError: ComputedRef<boolean>;
  toggleCardStatus: (status: "ACTIVE" | "BLOCKED") => Promise<void>;
  createVirtualCard: () => Promise<void>;
} {
  const { t } = useI18n();

  const { summary } = useCreditLineSummary();
  const store = useStore();

  const selectedCard = computed<ICardInfo | undefined>(() => {
    const virtualCards =
      store.getters["creditLine/getCardsByType"]("VIRTUAL") ?? [];

    if (virtualCards.length === 0) {
      return {
        status: PayJoyCardStatus.NONE,
        type: "VIRTUAL",
      } as ICardInfo;
    }

    return virtualCards[virtualCards.length - 1];
  });
  const disableCard = ref<boolean | false>(
    selectedCard.value?.status === PayJoyCardStatus.BLOCKED
  );
  const payjoyCardStatus = computed<PayJoyCardStatus | undefined>(() => {
    return selectedCard.value?.status;
  });
  const creditLineId = computed(() => {
    return summary.value?.creditLineId;
  });

  const alertMessage = ref<string>();
  const showAlert = ref<boolean>(false);

  const ccDeliveryError = computed(
    () =>
      payjoyCardStatus.value === PayJoyCardStatus.DELIVERY_FAILED ||
      payjoyCardStatus.value === PayJoyCardStatus.REPORTED_UNDELIVERED
  );

  const toggleCardStatus = async (status: "ACTIVE" | "BLOCKED") => {
    try {
      await store.dispatch("creditLine/setCardStatus", {
        status,
        lastFour: selectedCard.value?.lastFour || "",
      });
    } catch (error) {
      alertMessage.value = t("CARDSTATUSTOGGLEERROR");
      showAlert.value = true;
      if (error.response && error.response.data?.errorCode) {
        setTimeout(() => {
          disableCard.value = false;
        }, 2500);
      } else {
        disableCard.value = false;
        ErrorHandler.handleNetworkErrorAndLog(error);
      }
    }
  };

  watch(payjoyCardStatus, (newCardStatus) => {
    disableCard.value = newCardStatus === PayJoyCardStatus.BLOCKED;
  });

  const createVirtualCard = async () => {
    await store.dispatch("creditLine/createVirtualCreditCard");
  };

  return {
    payjoyCardStatus,
    disableCard,
    selectedCard,
    creditLineId,
    alertMessage,
    showAlert,
    ccDeliveryError,
    toggleCardStatus,
    createVirtualCard,
  };
}
